import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { ConfirmDialog } from '../components/confirm-dialog.js';
import { CreateBrandForm } from '../components/create-brand-form.js';
import { magToast } from '../components/toaster.js';
import { WorkspaceNav } from '../components/workspace-nav.js';
import { setActiveBrandId, useActiveBrandId } from '../lib/active-brand.js';
import { type Brand, deleteBrandApi, type MeResponse } from '../lib/api.js';
import { useDocumentTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandsQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// D1 Phase 3 — manage brands. Founders land here from the TopNav
// switcher's "manage brands" link or by typing the URL. Today: list
// every brand under the org, rename inline, upload logo, archive,
// create a new one.

interface BrandsSearch {
  /** `?new=1` opens the create-brand form by default. The top-nav
   *  brand-switcher's "add new brand" link sets this so the founder
   *  lands directly in the form instead of having to click "+ new
   *  brand" again. */
  new?: '1' | undefined;
}

export const Route = createFileRoute('/brands')({
  validateSearch: (search: Record<string, unknown>): BrandsSearch =>
    search['new'] === '1' ? { new: '1' } : {},
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    // [designer-role] Designer-only users don't have a brand picker —
    // they have a slot queue. Bounce to /designer instead so the
    // brand list isn't a teaser they can't act on.
    if (me.designerOnly) throw redirect({ to: '/designer' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    return { me, brands };
  },
  loader: ({ context }) => context as { me: MeResponse; brands: Brand[] },
  component: BrandsPage,
});

function BrandsPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { brands, me } = Route.useLoaderData();
  const search = Route.useSearch();
  const activeBrandId = useActiveBrandId();
  useDocumentTitle(['brands']);
  const live = brands.filter((b) => b.archivedAt === null);
  const archived = brands.filter((b) => b.archivedAt !== null);
  // Per the team access design doc (CommitMind) §"Settings hierarchy" — only
  // owners/admins can create or archive brands. Workspace members
  // (editor/viewer/manager-on-a-brand) hit this page as a brand
  // picker, not a setup surface.
  const canManageBrands = me.workspaceRole === 'owner' || me.workspaceRole === 'admin';

  const refresh = async () => {
    await refreshQueries(queryClient, ['brands']);
    await router.invalidate();
  };

  const isEmpty = live.length === 0 && archived.length === 0;
  // s162.27 — the create form used to sit perma-mounted above the
  // brand list, which made it the visual entry point even though the
  // founder almost always lands here to switch brands, not to make a
  // new one. now it's a header CTA that toggles a paper-tinted form,
  // collapsed by default unless the workspace has no brands yet OR
  // the founder arrived via the top-nav "add new brand" link
  // (?new=1) which jumps them straight into the form.
  const [creating, setCreating] = useState((isEmpty || search.new === '1') && canManageBrands);
  // One way to start a brand: paste an existing site. The no-site path — name
  // + a Mag interview that researches the brand and builds it a
  // <handle>.magig.site — is OFF for now by founder decision. The component
  // (interview-onboarding.tsx) and its api endpoints are intact; this is the
  // only place that reached them, so restoring the toggle here re-enables it.

  const closeCreate = () => {
    setCreating(false);
    // Strip the ?new=1 marker so a subsequent toggle doesn't re-open
    // the form on a back/forward nav.
    if (search.new === '1') {
      void navigate({ to: '/brands', search: {} });
    }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <WorkspaceNav active="brands" />
      <section className="mx-auto max-w-[860px] px-4 pb-16 pt-8 sm:px-7">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>brands</Trans>
            </p>
            <h1 className="mt-2 font-display text-[28px] font-black leading-[1.05] tracking-[-0.04em] sm:text-[34px]">
              {isEmpty ? (
                canManageBrands ? (
                  <Trans>let's set up your first brand.</Trans>
                ) : (
                  <Trans>nothing to show yet.</Trans>
                )
              ) : (
                <Trans>one workspace, many brands.</Trans>
              )}
            </h1>
            <p className="mt-3 max-w-[600px] text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
              {isEmpty ? (
                canManageBrands ? (
                  <Trans>
                    every brand under your workspace has its own brief, voice, ad accounts, and
                    feed. paste your site and mag'll read it, name the brand, and pull a brief
                    together.
                  </Trans>
                ) : (
                  <Trans>
                    you don't have access to any brands here. ask an admin to grant you one.
                  </Trans>
                )
              ) : (
                <Trans>
                  each brand has its own brief, its own voice, its own ad accounts, and its own
                  feed. mag works inside whichever brand you have active in the top nav.
                </Trans>
              )}
            </p>
          </div>
          {!isEmpty && canManageBrands ? (
            <button
              type="button"
              onClick={() => (creating ? closeCreate() : setCreating(true))}
              className={creating ? 'btn btn-ghost shrink-0 disabled:opacity-60' : 'btn shrink-0'}
              aria-expanded={creating}
            >
              {creating ? <Trans>cancel</Trans> : <Trans>+ new brand</Trans>}
              {!creating ? <span className="btn-arrow">→</span> : null}
            </button>
          ) : null}
        </header>

        {creating && canManageBrands ? (
          <div className="mt-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
            <CreateBrandForm
              onCreated={async () => {
                await refresh();
                closeCreate();
              }}
            />
          </div>
        ) : null}

        {live.length > 0 ? (
          <ul className="mt-8 flex flex-col gap-3.5">
            {live.map((b) => (
              <BrandRow
                key={b.id}
                brand={b}
                isActive={b.id === activeBrandId || (!activeBrandId && b === live[0])}
                onChanged={refresh}
              />
            ))}
          </ul>
        ) : null}

        {archived.length > 0 ? (
          <details className="mt-10">
            <summary className="cursor-pointer font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]/80">
              <Trans>↘ archived · {archived.length}</Trans>
            </summary>
            <ul className="mt-3 flex flex-col gap-3 opacity-75">
              {archived.map((b) => (
                <BrandRow key={b.id} brand={b} isActive={false} onChanged={refresh} />
              ))}
            </ul>
          </details>
        ) : null}
      </section>
    </main>
  );
}

/** A whole-row link into the brand. Live brands open the home; archived
 *  brands open settings so the founder can restore (or delete) from
 *  there. All other config (rename, logo, archive, delete) lives in
 *  /brand/$slug/settings now — /brands is a chooser, not a control
 *  panel. */
function BrandRow({
  brand,
  isActive,
  onChanged,
}: {
  brand: Brand;
  isActive: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useLingui();

  const isArchived = brand.archivedAt !== null;
  const target = isArchived
    ? ({ to: '/brand/$slug/settings', params: { slug: brand.slug } } as const)
    : ({ to: '/brand/$slug/home', params: { slug: brand.slug } } as const);
  // Only surface the delete affordance for brands the founder never
  // finished onboarding for — same row most likely to be a typo create.
  // Real brands keep delete behind /brand/$slug/settings's type-the-
  // name confirm so it stays hard to nuke by accident.
  const canQuickDelete = brand.briefReady === false && !isArchived;
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleQuickDelete = async () => {
    setDeleting(true);
    try {
      await deleteBrandApi(brand.id);
      magToast.success(`${brand.name} deleted.`, 'nothing was attached to it yet.');
      setConfirming(false);
      await onChanged();
    } catch (err) {
      magToast.error("couldn't delete.", founderErrorCopy(err));
    } finally {
      setDeleting(false);
    }
  };

  // s162.27 — brand-aligned chrome: chunky 1.5px ink border + spell
  // shadow that matches the new primary-card pattern used elsewhere
  // (identity / site-context / followups). Hover lifts the shadow
  // (4 → 6px) so the row reads as clickable without nudging the
  // whole grid. Active brand gets a plum-tinted shadow + chip so
  // the founder can spot which workspace they're in at a glance.
  const shadowClass = isActive
    ? 'shadow-[4px_4px_0_var(--plum)] hover:shadow-[6px_6px_0_var(--plum)]'
    : 'shadow-[4px_4px_0_var(--spell)] hover:shadow-[6px_6px_0_var(--spell)]';
  return (
    <li
      className={`group/row flex items-stretch overflow-hidden rounded-2xl border-[1.5px] border-[var(--ink)] bg-white transition-all ${shadowClass}`}
    >
      <Link
        {...target}
        onClick={() => setActiveBrandId(brand.id)}
        className="group flex flex-1 items-center gap-4 px-5 py-4 no-underline sm:px-6 sm:py-5"
      >
        <BrandAvatar brand={brand} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate font-display text-[20px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--ink)]">
              {brand.name}
            </h2>
            {isActive ? (
              <span className="inline-flex items-center rounded-full bg-[var(--plum)] px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.12em] text-[var(--cream)]">
                <Trans>active</Trans>
              </span>
            ) : null}
            {isArchived ? (
              <span className="inline-flex items-center rounded-full border border-[var(--line-strong)] bg-[var(--paper)] px-2 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/65">
                <Trans>archived</Trans>
              </span>
            ) : null}
            {canQuickDelete ? (
              <span className="inline-flex items-center rounded-full border border-[var(--pulse)]/40 bg-[var(--pulse)]/10 px-2 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--pulse)]">
                <Trans>brief pending</Trans>
              </span>
            ) : null}
          </div>
          <p className="mt-1 truncate font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
            <span className="text-[var(--ink)]/40">{'/brand/'}</span>
            <span className="text-[var(--ink)]/80">{brand.slug}</span>
          </p>
        </div>
        <span
          aria-hidden
          className="font-mono text-[20px] text-[var(--ink)]/30 transition-all group-hover:translate-x-0.5 group-hover:text-[var(--plum)]"
        >
          →
        </span>
      </Link>
      {canQuickDelete ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={deleting}
          aria-label={`delete ${brand.name}`}
          title={t`delete this brand · onboarding never finished`}
          className="grid w-12 place-items-center border-l border-[var(--ink)]/10 text-[var(--ink)]/35 transition-colors hover:bg-[var(--pulse)]/12 hover:text-[var(--pulse)] disabled:opacity-60"
        >
          {deleting ? <span className="font-mono text-[12px]">…</span> : <TrashIcon />}
        </button>
      ) : null}
      <ConfirmDialog
        open={confirming}
        title={`delete ${brand.name}?`}
        body={
          <>
            <Trans>
              the brief on this brand was never filled in, so there's nothing to lose — but the row
              itself can't be undone.
            </Trans>
          </>
        }
        confirmLabel={t`delete forever`}
        busyLabel={t`deleting…`}
        destructive
        busy={deleting}
        onConfirm={handleQuickDelete}
        onCancel={() => setConfirming(false)}
      />
    </li>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="16"
      viewBox="0 0 14 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1.5 3.5h11" />
      <path d="M5.5 3.5V2a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5" />
      <path d="M2.75 3.5l.625 10A1 1 0 0 0 4.37 14.5h5.26a1 1 0 0 0 .995-1L11.25 3.5" />
      <path d="M5.75 6.5v5M8.25 6.5v5" />
    </svg>
  );
}

function BrandAvatar({ brand }: { brand: Brand }) {
  // Avatar bumped 36 → 56px so the brand reads as hero content on
  // the picker (not a row-level glyph). Ink border at primary
  // weight echoes the surrounding card chrome. Logo source mirrors
  // the BrandSwitcher chip (top-nav.tsx): favicon first, full logo
  // as fallback, brand initial last — so the workspace brands page
  // and the in-brand header chip always render the same mark.
  const src = brand.faviconUrl ?? brand.logoUrl;
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="h-14 w-14 shrink-0 rounded-xl border-[1.5px] border-[var(--ink)] bg-white object-cover"
      />
    );
  }
  const initial = brand.name.trim().charAt(0).toUpperCase() || '✦';
  return (
    <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border-[1.5px] border-[var(--ink)] bg-[var(--paper)] font-mono text-[22px] font-bold text-[var(--ink)]/80">
      {initial}
    </span>
  );
}
